/**
 * Access roles. A person is not a role: the same human can be evaluated in one
 * cycle, rate three colleagues, and coach a fourth. Roles are always held
 * *within an organization*, and rater access is scoped to a single survey.
 */
export const USER_ROLES = ['ADMIN', 'HR', 'COACH', 'PARTICIPANT', 'RATER'];
/**
 * Capabilities, so route guards read as intent rather than as role lists.
 * `report:read:own` and `report:read:assigned` are deliberately distinct from
 * a general read: nobody has blanket access to reports.
 */
export const PERMISSIONS = [
    'organization:manage',
    'cycle:manage',
    'framework:manage',
    'participant:manage',
    'rater-map:import',
    /**
     * Taking somebody off a rater's list after the map has been imported.
     *
     * Deliberately not `rater-map:import`, which P&O hold: that one loads the
     * client's own spreadsheet into a cycle that is still a DRAFT, and the
     * spreadsheet is theirs. This edits the map of a *live* cycle, one pair at a
     * time, while people are answering — and every removal changes the
     * arithmetic behind somebody's report, because a segment that drops below
     * the disclosure threshold stops being shown separately. That is the
     * consultancy's call to make and to explain, so it is admin only.
     */
    'rater-map:edit',
    'survey:answer',
    'survey:close',
    'progress:read',
    'report:read:own',
    'report:read:assigned',
    /// Admin and coach: every report, at any stage, with nothing folded away.
    'report:read:all',
    /**
     * An approved report with nothing anybody wrote about the person in it.
     *
     * Held by nobody today. It was P&O's, and the client's decision is that they
     * do not read reports at all — they run the cycle, they do not receive its
     * findings. Kept as a capability rather than deleted because the redaction
     * behind it is real and tested, and the day somebody internal is meant to
     * read a report, this is the shape it should take.
     */
    'report:read:redacted',
    /**
     * Opening and closing the cycle.
     *
     * Separate from `cycle:manage`, which is about setting one up. This is the
     * switch that decides whether ninety-two people can answer right now:
     * opening starts the collection and freezes the 360 map, closing stops
     * somebody mid-questionnaire. Admin only, by the client's decision.
     */
    'cycle:open',
    /// Seeing who was asked to assess whom, and how far each of them has got.
    'rater:read',
    /**
     * Correcting an address, setting a language, and sending somebody their link.
     *
     * Admin only, by the client's decision. Both halves are the same authority
     * wearing two hats: whoever can change where an invitation goes can send a
     * live survey link to an address of their choosing, so splitting "edit" from
     * "send" would be a line on a diagram rather than a line in the product.
     */
    'rater:invite',
    /**
     * Writing to everybody who has never been invited, in one go.
     *
     * Separate from `rater:invite` because the blast radius is different in
     * kind: one is an email to a named person you just looked at, the other is
     * ninety of them to an address list nobody re-read first. Also admin only,
     * so today the two travel together — they are kept apart because the day
     * somebody is trusted with one of them, it will be this one they are not.
     */
    'rater:invite:bulk',
    /// Seeing who the coaches are and who each of them works with.
    'coach:read',
    /**
     * Creating coaches and deciding who each of them works with.
     *
     * Admin only, and deliberately apart from `coach:read`. An assignment is not
     * bookkeeping: it is the whole of what a coach can see, so whoever can edit
     * the list can grant themselves — or anybody — sight of any leader's report.
     */
    'coach:manage',
    /// Creating the client's own P&O accounts and sending them their access.
    'staff:manage',
    'report:review',
    'report:publish',
    /**
     * The synthesis of what colleagues wrote: drafting it, rewriting it, and
     * signing it off.
     *
     * Held by nobody but an admin, and deliberately apart from `report:review`.
     * That text is the only form in which what people wrote reaches the person
     * they wrote about, and it is produced by a model from sentences the leader
     * will never see. Drafting it, changing a theme several colleagues raised,
     * or declaring it fit to read are the same authority over the same words,
     * and none of them leaves a trace in the report — so they sit with whoever
     * answers for the instrument rather than with whoever is in the room for the
     * conversation it feeds.
     *
     * The coach still reads it, still reads the sentences behind it, and still
     * approves the report itself under `report:publish`. What they no longer do
     * is author this part of it.
     */
    'summary:manage',
    'audit:read',
];
/**
 * Four working roles, and the lines between them.
 *
 * ADMIN does everything. The other three are each missing something on
 * purpose, and each omission is somebody's decision rather than an oversight:
 *
 *  - COACH works with the leaders assigned to them, and only those. For those
 *    leaders they read the report at any stage with nothing blocked and
 *    nothing folded — making sense of a thin cohort is the job — but the set
 *    of leaders is decided by an admin, not by holding the role. A coach with
 *    no assignments sees an empty list, which is correct rather than broken.
 *  - HR (P&O) runs the cycle: the roster, the map, closing the surveys, and
 *    watching participation. What they do not get is `rater:invite` either.
 *    Nothing leaves the building over their signature — not one invitation and
 *    certainly not ninety — because everything the client's own staff send to
 *    the client's own staff about the client's own leaders is the consultancy
 *    speaking. They see every address; they change none. They read a report
 *    only once it is approved, and never a word anybody wrote — see
 *    feedbackForAudience(). An internal HR team is the reader every rater had
 *    in mind when they were promised their answers would be combined.
 *  - PARTICIPANT sees one report, their own, once it is published. Nothing
 *    else in the product exists for them.
 *
 * RATER holds `survey:answer` and nothing else. Their whole relationship with
 * the system is one link and one questionnaire.
 */
export const ROLE_PERMISSIONS = {
    ADMIN: PERMISSIONS,
    HR: [
        'cycle:manage',
        'participant:manage',
        'rater-map:import',
        'survey:close',
        'progress:read',
        // No report of any kind. P&O run the cycle — the roster, the map, the
        // chasing — and see how far it has got, but a 360 report is between the
        // leader and their coach. Every rater was promised their answers would be
        // combined before anybody read them; the shortest way to keep that promise
        // is for the client's own staff not to be readers at all.
        // Reads the whole rater list, addresses included. Sends nothing, edits no
        // address: see `rater:invite` below.
        'rater:read',
        // Sees who is coaching whom. Cannot create one, invite one, or move a
        // leader between them: that is the consultancy's side of the programme.
        'coach:read',
    ],
    /**
     * A coach works with the leaders assigned to them, and sees the programme
     * around those conversations.
     *
     * `report:read:all` is about *depth*, not reach: for the leaders they coach,
     * nothing is blocked by a thin cohort and nothing is folded away, because
     * making sense of a thin cohort is the job. Which leaders those are is a
     * separate question, answered by their assignments — see coachedBy().
     *
     * No `rater:read`: chasing who has answered is P&O's work, and a coach does
     * not need a directory of everyone's addresses to prepare a conversation.
     */
    COACH: [
        'progress:read',
        'report:read:assigned',
        'report:read:all',
        'report:review',
        'report:publish',
    ],
    PARTICIPANT: ['survey:answer', 'report:read:own'],
    RATER: ['survey:answer'],
};
export const hasPermission = (roles, permission) => roles.some((role) => ROLE_PERMISSIONS[role].includes(permission));
/** Supported interface and content locales. */
export const LOCALES = ['es', 'en'];
export const DEFAULT_LOCALE = 'es';
/**
 * The address the 360 map import writes when it has none.
 *
 * The client's map arrived without email addresses, so every person starts
 * with one of these. Nothing can be sent to it, and the difference between
 * "this person has no address" and "this person was invited and ignored it" is
 * one P&O has to be able to see — so it is a shared constant rather than a
 * string repeated in whichever file happens to need it.
 */
export const PLACEHOLDER_EMAIL_DOMAIN = '@pending.invalid';
export const isPlaceholderEmail = (email) => email.trim().toLowerCase().endsWith(PLACEHOLDER_EMAIL_DOMAIN);
//# sourceMappingURL=roles.js.map